<div class="main">
    <div class="settings-header">
        <div class="main-title">
            <h1>Company Information</h1>
            <p>Manage your company details and contact information</p>
        </div>
        <div class="header-actions">
            <button
                *ngIf="!isEditMode"
                type="button"
                class="btn-action-primary"
                (click)="toggleEditMode()"
            >
                <i class="fas fa-edit"></i>
                Edit Information
            </button>
        </div>
    </div>

    <div class="settings-content">
        <!-- Company Information Card -->
        <div class="settings-card">
            <div class="card-header">
                <div class="header-content">
                    <h2><i class="fas fa-building"></i> Company Details</h2>
                    <p class="card-subtitle">
                        View and update your company information
                    </p>
                </div>
            </div>

            <form
                [formGroup]="companyForm"
                (ngSubmit)="onUpdate()"
                class="settings-form"
            >
                <!-- Company Name -->
                <div class="form-section">
                    <h3 class="section-title">
                        <i class="fas fa-briefcase"></i>
                        Basic Information
                    </h3>

                    <div class="form-row">
                        <div class="form-group full-width">
                            <label for="companyName">
                                Company Name
                                <span class="text-danger-required">*</span>
                            </label>
                            <input
                                type="text"
                                id="companyName"
                                class="form-control"
                                formControlName="companyName"
                                placeholder="Enter company name"
                                [class.read-only]="!isEditMode"
                            />
                            <div
                                *ngIf="
                                    formControls['companyName'].touched &&
                                    formControls['companyName'].invalid
                                "
                                class="text-danger"
                            >
                                <span
                                    *ngIf="
                                        formControls['companyName'].errors?.[
                                            'required'
                                        ]
                                    "
                                    >Company name is required.</span
                                >
                                <span
                                    *ngIf="
                                        formControls['companyName'].errors?.[
                                            'minlength'
                                        ]
                                    "
                                    >Company name must be at least 2
                                    characters.</span
                                >
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Location Information -->
                <div class="form-section">
                    <h3 class="section-title">
                        <i class="fas fa-map-marker-alt"></i>
                        Location
                    </h3>

                    <div class="form-row">
                        <div class="form-group full-width">
                            <label for="address">
                                Address
                                <span class="text-danger-required">*</span>
                            </label>
                            <input
                                type="text"
                                id="address"
                                class="form-control"
                                formControlName="address"
                                placeholder="Enter street address"
                                [class.read-only]="!isEditMode"
                            />
                            <div
                                *ngIf="
                                    formControls['address'].touched &&
                                    formControls['address'].invalid
                                "
                                class="text-danger"
                            >
                                <span
                                    *ngIf="
                                        formControls['address'].errors?.[
                                            'required'
                                        ]
                                    "
                                    >Address is required.</span
                                >
                                <span
                                    *ngIf="
                                        formControls['address'].errors?.[
                                            'minlength'
                                        ]
                                    "
                                    >Address must be at least 5
                                    characters.</span
                                >
                            </div>
                        </div>
                    </div>

                    <div class="form-row">
                        <div class="form-group">
                            <label for="city">
                                City <span class="text-danger-required">*</span>
                            </label>
                            <input
                                type="text"
                                id="city"
                                class="form-control"
                                formControlName="city"
                                placeholder="Enter city"
                                [class.read-only]="!isEditMode"
                            />
                            <div
                                *ngIf="
                                    formControls['city'].touched &&
                                    formControls['city'].invalid
                                "
                                class="text-danger"
                            >
                                <span
                                    *ngIf="
                                        formControls['city'].errors?.[
                                            'required'
                                        ]
                                    "
                                    >City is required.</span
                                >
                                <span
                                    *ngIf="
                                        formControls['city'].errors?.[
                                            'minlength'
                                        ]
                                    "
                                    >City must be at least 2 characters.</span
                                >
                            </div>
                        </div>

                        <div class="form-group">
                            <label for="country">
                                Country
                                <span class="text-danger-required">*</span>
                            </label>
                            <input
                                type="text"
                                id="country"
                                class="form-control"
                                formControlName="country"
                                placeholder="Enter country"
                                [class.read-only]="!isEditMode"
                            />
                            <div
                                *ngIf="
                                    formControls['country'].touched &&
                                    formControls['country'].invalid
                                "
                                class="text-danger"
                            >
                                <span
                                    *ngIf="
                                        formControls['country'].errors?.[
                                            'required'
                                        ]
                                    "
                                    >Country is required.</span
                                >
                                <span
                                    *ngIf="
                                        formControls['country'].errors?.[
                                            'minlength'
                                        ]
                                    "
                                    >Country must be at least 2
                                    characters.</span
                                >
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Contact Information -->
                <div class="form-section">
                    <h3 class="section-title">
                        <i class="fas fa-phone"></i>
                        Contact Details
                    </h3>

                    <div class="form-row">
                        <div class="form-group">
                            <label for="phone">
                                Phone Number
                                <span class="text-danger-required">*</span>
                            </label>
                            <input
                                type="tel"
                                id="phone"
                                class="form-control"
                                formControlName="phoneNumber"
                                pattern="\d*"
                                inputmode="numeric"
                                placeholder="0XXXXXXXXX"
                                [class.read-only]="!isEditMode"
                            />
                            <div
                                *ngIf="
                                    formControls['phoneNumber'].touched &&
                                    formControls['phoneNumber'].invalid
                                "
                                class="text-danger"
                            >
                                <span
                                    *ngIf="
                                        formControls['phoneNumber'].errors?.[
                                            'required'
                                        ]
                                    "
                                    >Phone number is required.</span
                                >
                                <span
                                    *ngIf="
                                        formControls['phoneNumber'].errors?.[
                                            'pattern'
                                        ]
                                    "
                                    >Invalid phone number. Must be 10
                                    digits.</span
                                >
                            </div>
                        </div>

                        <div class="form-group">
                            <label for="email">
                                Email Address
                                <span class="text-danger-required">*</span>
                            </label>
                            <input
                                type="email"
                                id="email"
                                class="form-control"
                                formControlName="emailAddress"
                                placeholder="company@example.com"
                                [class.read-only]="!isEditMode"
                            />
                            <div
                                *ngIf="
                                    formControls['emailAddress'].touched &&
                                    formControls['emailAddress'].invalid
                                "
                                class="text-danger"
                            >
                                <span
                                    *ngIf="
                                        formControls['emailAddress'].errors?.[
                                            'required'
                                        ]
                                    "
                                    >Email is required.</span
                                >
                                <span
                                    *ngIf="
                                        formControls['emailAddress'].errors?.[
                                            'pattern'
                                        ]
                                    "
                                    >Invalid email format.</span
                                >
                            </div>
                        </div>
                    </div>

                    <div class="form-row">
                        <div class="form-group full-width">
                            <label for="website">
                                Website
                                <span class="optional-label">(Optional)</span>
                            </label>
                            <input
                                type="text"
                                id="website"
                                class="form-control"
                                formControlName="website"
                                placeholder="www.smartpoultry.com"
                                [class.read-only]="!isEditMode"
                            />
                        </div>
                    </div>
                </div>

                <!-- Action Buttons -->
                <div class="form-actions" *ngIf="isEditMode">
                    <button
                        type="button"
                        class="btn close-btn-secondary"
                        (click)="onCancel()"
                        [disabled]="isLoading"
                    >
                        <i class="fas fa-times"></i>
                        Cancel
                    </button>
                    <button
                        type="submit"
                        class="btn-action-primary"
                        [disabled]="isLoading || companyForm.invalid"
                    >
                        <i class="fas fa-save"></i>
                        {{ isLoading ? 'Saving...' : 'Save Changes' }}
                    </button>
                </div>
            </form>
        </div>
    </div>
</div>
